import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Calendar, Pencil, X, Package, ListChecks, Minus, UploadSimple, Image as ImageIcon, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MenuSelector from '../components/MenuSelector';
import ComboSelector from '../components/ComboSelector';
import PhotographerSelector from '../components/PhotographerSelector';
import DecoratorSelector from '../components/DecoratorSelector';
import IdBadge from '../components/IdBadge';
import VendorOffersCard from '../components/VendorOffersCard';
import PlatformCommissionBanner from '../components/PlatformCommissionBanner';
import KycCard from '../components/KycCard';
import JoiningFeeCard from '../components/JoiningFeeCard';
import ServingCitiesCard from '../components/ServingCitiesCard';
import CatererServiceStylesCard from '../components/CatererServiceStylesCard';
import CatererMenuCard from '../components/CatererMenuCard';
import CatererServicePriceCard from '../components/CatererServicePriceCard';
import CelebrateMusicSelector from '../components/CelebrateMusicSelector';
import ProPortfolioCard from '../components/ProPortfolioCard';
import MetaSocialConnectCard from '../components/MetaSocialConnectCard';
import GoogleMapsImportCard from '../components/GoogleMapsImportCard';
import CustomerInquiriesPanel from '../components/CustomerInquiriesPanel';
import AdminFilledContentBanner from '../components/AdminFilledContentBanner';
import BuyLeadsCard from '../components/BuyLeadsCard';
import VendorProductsPanel from '../components/VendorProductsPanel';
import CakePackagesCard from '../components/CakePackagesCard';
import VendorCatalogEditor from '../components/VendorCatalogEditor';
import ProPendingOffersCard from '../components/ProPendingOffersCard';
import VendorPendingInquiriesCard from '../components/VendorPendingInquiriesCard';
import VenueSelector from '../components/VenueSelector';
import DJEntertainmentSelector from '../components/DJEntertainmentSelector';
import BirthdayDecoratorSelector from '../components/BirthdayDecoratorSelector';
import CustomDecorPackages from '../components/CustomDecorPackages';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import DeleteAccountSection from '../components/DeleteAccountSection';
import Footer from '../components/Footer';
import InviteAndEarn from '../components/InviteAndEarn';
import ProAttendanceWidget from '../components/ProAttendanceWidget';
import CalendarAvailability from '../components/CalendarAvailability';
import BookingsBoard from '../components/BookingsBoard';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_GALLERY = 10;

const PhotographerGallery = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/vendor/gallery`, { withCredentials: true });
      setImages(data.images || []);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    }
  };

  const saveGallery = async (next) => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/vendor/gallery`, { images: next }, { withCredentials: true });
      setImages(data.images);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update gallery');
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const remaining = MAX_GALLERY - images.length;
    if (remaining <= 0) {
      toast.error(`You've reached the ${MAX_GALLERY}-image limit`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > toUpload.length) {
      toast.warning(`Only ${toUpload.length} image(s) uploaded — gallery limit is ${MAX_GALLERY}`);
    }
    setUploading(true);
    const uploaded = [];
    for (const file of toUpload) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      } catch (err) {
        toast.error(`${file.name}: upload failed`);
      }
    }
    if (uploaded.length > 0) {
      const next = [...images, ...uploaded];
      await saveGallery(next);
      toast.success(`${uploaded.length} image(s) added to gallery`);
    }
    setUploading(false);
  };

  const removeImage = async (idx) => {
    const next = images.filter((_, i) => i !== idx);
    await saveGallery(next);
    toast.success('Image removed');
  };

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="photographer-gallery">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImageIcon size={20} className="text-brand-primary" /> Work Gallery
        </CardTitle>
        <CardDescription>
          Showcase up to {MAX_GALLERY} images of your best work. {images.length}/{MAX_GALLERY} used.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
          {images.map((src, idx) => (
            <div key={idx} className="relative aspect-square border border-[#E8E1D5] overflow-hidden group" data-testid={`gallery-img-${idx}`}>
              <img src={src} alt={`Work ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-1 right-1 w-7 h-7 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`gallery-remove-${idx}`}
                title="Remove"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}

          {images.length < MAX_GALLERY && (
            <label
              className={`aspect-square border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
                uploading ? 'border-text-secondary/30 bg-sand-50/50' : 'border-brand-primary/40 hover:border-brand-primary bg-brand-primary/5'
              }`}
              data-testid="gallery-upload-tile"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                data-testid="gallery-upload-input"
              />
              <UploadSimple size={28} className="text-brand-primary" />
              <span className="text-xs text-text-secondary font-medium">
                {uploading ? 'Uploading...' : 'Add Images'}
              </span>
            </label>
          )}
        </div>

        {images.length === 0 && !uploading && (
          <p className="text-sm text-text-secondary italic">No images yet. Upload your best work to showcase your portfolio.</p>
        )}
        {images.length >= MAX_GALLERY && (
          <p className="text-xs text-amber-700 font-medium">Gallery full. Remove an image to upload a new one.</p>
        )}
      </CardContent>
    </Card>
  );
};



const VendorDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showMenuSelector, setShowMenuSelector] = useState(false);
  const [showComboSelector, setShowComboSelector] = useState(false);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    category: user?.category || '',
    description: '',
    base_price: '',
    event_type: ['wedding', 'birthday'],
    packages: [],
  });

  // Category-specific items
  const [menuItems, setMenuItems] = useState([{ name: '', price: '' }]);
  const [combos, setCombos] = useState([{ name: '', items: '', price: '' }]);
  // Photographer: array of editable packages { tier, name, description, price, items: [{name, category, qty}] }
  const [photoPackages, setPhotoPackages] = useState([]);
  // Decorator: { marriage_types, venue_types, decoration_services, addon_services }
  const [decorData, setDecorData] = useState(null);
  const [customDecorPackages, setCustomDecorPackages] = useState(null); // { packages: [...] }
  const [showDecorSelector, setShowDecorSelector] = useState(false);
  // Venue: { venues: [...] }
  const [venueData, setVenueData] = useState(null);
  const [showVenueSelector, setShowVenueSelector] = useState(false);
  // DJ: { entertainment_types: [...] }
  const [djData, setDjData] = useState(null);
  const [showDjSelector, setShowDjSelector] = useState(false);
  // Birthday Decorator: { themes: [...] }
  const [birthdayDecorData, setBirthdayDecorData] = useState(null);
  const [showBirthdayDecorSelector, setShowBirthdayDecorSelector] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, bookingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/services/vendor/${user.id}`),
        axios.get(`${BACKEND_URL}/api/bookings/vendor`, { withCredentials: true }),
      ]);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const resetForm = () => {
    setServiceFormData({
      name: '',
      category: user?.category || '',
      description: '',
      base_price: '',
      event_type: ['wedding', 'birthday'],
      packages: [],
    });
    setMenuItems([{ name: '', price: '' }]);
    setCombos([{ name: '', items: '', price: '' }]);
    setPhotoPackages([]);
    setDecorData(null);
    setCustomDecorPackages(null);
    setVenueData(null);
    setDjData(null);
    setBirthdayDecorData(null);
    setEditingService(null);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceFormData({
      name: service.name,
      category: service.category,
      description: service.description,
      base_price: service.base_price,
      event_type: service.event_type || ['wedding', 'birthday'],
      packages: service.packages || [],
    });

    // Load category-specific data
    if (service.packages && service.packages.length > 0) {
      const pkg = service.packages[0];
      if (pkg.menu_items) setMenuItems(pkg.menu_items);
      if (pkg.combos) setCombos(pkg.combos);
      if (pkg.photo_packages) setPhotoPackages(pkg.photo_packages);
      if (pkg.decor_data) setDecorData(pkg.decor_data);
      if (pkg.custom_decor_packages) setCustomDecorPackages(pkg.custom_decor_packages);
      if (pkg.venue_data) setVenueData(pkg.venue_data);
      if (pkg.dj_data) setDjData(pkg.dj_data);
      if (pkg.birthday_decor_data) setBirthdayDecorData(pkg.birthday_decor_data);
    }

    setShowServiceForm(true);
  };

  const handleCreateOrUpdateService = async (e) => {
    e.preventDefault();

    let categoryData = {};
    
    // Build category-specific packages
    if (user.category === 'caterer') {
      categoryData = {
        menu_items: menuItems.filter(item => item.name && item.price),
        combos: combos.filter(combo => combo.name && combo.price)
      };
    } else if (user.category === 'photographer') {
      categoryData = {
        photo_packages: photoPackages
      };
    } else if (user.category === 'decorator') {
      // Decorators can list both wedding decor + (optionally) birthday themes
      const decoratorData = {};
      if (decorData) decoratorData.decor_data = decorData;
      if (customDecorPackages && customDecorPackages.packages?.length > 0) {
        decoratorData.custom_decor_packages = customDecorPackages;
      }
      if (birthdayDecorData && birthdayDecorData.themes?.length > 0) {
        decoratorData.birthday_decor_data = birthdayDecorData;
      }
      if (Object.keys(decoratorData).length > 0) {
        categoryData = decoratorData;
      }
    } else if (user.category === 'venue') {
      if (venueData) {
        categoryData = { venue_data: venueData };
      }
    } else if (user.category === 'dj') {
      if (djData) {
        categoryData = { dj_data: djData };
      }
    } else if (user.category === 'birthday_decorator') {
      if (birthdayDecorData) {
        categoryData = { birthday_decor_data: birthdayDecorData };
      }
    }

    const packages = Object.keys(categoryData).length > 0 ? [categoryData] : [];

    // Auto-generate service name and base price from quick-add data
    let autoName = serviceFormData.name || `${user.category.charAt(0).toUpperCase() + user.category.slice(1)} Service`;
    let autoPrice = parseFloat(serviceFormData.base_price) || 0;

    if (user.category === 'caterer' && !serviceFormData.name) {
      const itemCount = menuItems.filter(i => i.name).length;
      const comboCount = combos.filter(c => c.name).length;
      autoName = `Catering Menu (${itemCount} items, ${comboCount} combos)`;
    } else if (user.category === 'photographer' && !serviceFormData.name) {
      autoName = `Photography (${photoPackages.length} packages)`;
      if (photoPackages.length > 0) autoPrice = autoPrice || photoPackages[0].price;
    } else if (user.category === 'decorator' && !serviceFormData.name && (decorData || birthdayDecorData || customDecorPackages)) {
      const pkgCount = decorData?.packages?.length || 0;
      const themeCount = birthdayDecorData?.themes?.length || 0;
      const cpCount = customDecorPackages?.packages?.length || 0;
      const parts = [];
      if (pkgCount) parts.push(`${pkgCount} package${pkgCount !== 1 ? 's' : ''}`);
      if (themeCount) parts.push(`${themeCount} birthday theme${themeCount !== 1 ? 's' : ''}`);
      if (cpCount) parts.push(`${cpCount} custom package${cpCount !== 1 ? 's' : ''}`);
      autoName = `Decoration (${parts.join(', ') || 'no items'})`;
    } else if (user.category === 'venue' && !serviceFormData.name && venueData) {
      autoName = `Venue (${venueData.venues?.length || 0} venue types)`;
    } else if (user.category === 'dj' && !serviceFormData.name && djData) {
      autoName = `DJ & Entertainment (${djData.entertainment_types?.length || 0} types)`;
    } else if (user.category === 'birthday_decorator' && !serviceFormData.name && birthdayDecorData) {
      autoName = `Birthday Decoration (${birthdayDecorData.themes?.length || 0} themes)`;
    }

    try {
      const payload = {
        name: autoName,
        category: user.category,
        description: serviceFormData.description || `${user.category} services`,
        base_price: autoPrice,
        event_type: serviceFormData.event_type || ['wedding', 'birthday'],
        packages,
        // Decorator-only: flat array of event-scoped packages for customer matching
        ...(user.category === 'decorator' && decorData?.packages?.length > 0
            ? { decorator_packages: decorData.packages }
            : {}),
      };

      if (editingService) {
        await axios.put(`${BACKEND_URL}/api/services/${editingService.id}`, payload, { withCredentials: true });
        toast.success('Service updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, payload, { withCredentials: true });
        toast.success('Service created successfully!');
      }

      setShowServiceForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/services/${serviceId}`, { withCredentials: true });
      toast.success('Service deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Booking status updated!');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const addMenuItem = () => setMenuItems([...menuItems, { name: '', price: '' }]);
  const removeMenuItem = (index) => setMenuItems(menuItems.filter((_, i) => i !== index));
  const updateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index][field] = value;
    setMenuItems(updated);
  };

  const addCombo = () => setCombos([...combos, { name: '', items: '', price: '' }]);
  const removeCombo = (index) => setCombos(combos.filter((_, i) => i !== index));
  const updateCombo = (index, field, value) => {
    const updated = [...combos];
    updated[index][field] = value;
    setCombos(updated);
  };

  const addEquipment = () => {};
  const removeEquipment = () => {};
  const updateEquipment = () => {};
  const addPhotographyService = () => {};
  const removePhotographyService = () => {};
  const updatePhotographyService = () => {};

  const handleQuickMenuSelect = (selectedMenu) => {
    setMenuItems(selectedMenu);
    setShowMenuSelector(false);
    toast.success(`${selectedMenu.length} items added to menu!`);
  };

  const handleQuickComboSelect = (combo) => {
    // Replace the initial empty combo or append to existing real combos
    const existingCombos = combos.filter(c => c.name && c.price);
    setCombos([...existingCombos, combo]);
    setShowComboSelector(false);
    toast.success(`Combo "${combo.name}" added!`);
  };

  const handlePhotoPackageAdd = (pkg) => {
    setPhotoPackages([...photoPackages, pkg]);
    setShowPhotoSelector(false);
    const scope = pkg.event_subtype_label
      ? `${pkg.event_type_label} — ${pkg.event_subtype_label}`
      : pkg.event_type_label || '';
    toast.success(`${pkg.name} package added${scope ? ` for ${scope}` : ''}!`);
  };

  const removePhotoPackage = (index) => setPhotoPackages(photoPackages.filter((_, i) => i !== index));

  const updatePhotoPackagePrice = (index, newPrice) => {
    const updated = [...photoPackages];
    updated[index].price = parseInt(newPrice) || 0;
    setPhotoPackages(updated);
  };

  const togglePhotoPackageItem = (pkgIndex, itemIndex) => {
    const updated = [...photoPackages];
    const item = updated[pkgIndex].items[itemIndex];
    if (item.qty > 0) {
      item.qty = 0;
    } else {
      item.qty = 1;
    }
    updated[pkgIndex].items = updated[pkgIndex].items.filter(i => i.qty > 0 || i === item);
    setPhotoPackages(updated);
  };

  const changePhotoPackageItemQty = (pkgIndex, itemIndex, delta) => {
    const updated = [...photoPackages];
    const item = updated[pkgIndex].items[itemIndex];
    item.qty = Math.max(0, item.qty + delta);
    setPhotoPackages(updated);
  };

  const removePhotoPackageItem = (pkgIndex, itemIndex) => {
    const updated = [...photoPackages];
    updated[pkgIndex].items = updated[pkgIndex].items.filter((_, i) => i !== itemIndex);
    setPhotoPackages(updated);
  };

  const handleDecorSave = (data) => {
    setDecorData(data);
    setShowDecorSelector(false);
    const n = data?.packages?.length || 0;
    toast.success(`${n} package${n === 1 ? '' : 's'} saved!`);
  };

  const handleVenueSave = (data) => {
    setVenueData(data);
    setShowVenueSelector(false);
    toast.success(`${data.venues?.length || 0} venue type(s) saved!`);
  };

  const handleDjSave = (data) => {
    setDjData(data);
    setShowDjSelector(false);
    toast.success(`${data.entertainment_types?.length || 0} entertainment type(s) saved!`);
  };

  const handleBirthdayDecorSave = (data) => {
    setBirthdayDecorData(data);
    setShowBirthdayDecorSelector(false);
    toast.success(`${data.themes?.length || 0} birthday theme(s) saved!`);
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" />
          <MobileNav
            links={[]}
            actions={[
              { onClick: logout, label: t('common.logout') || 'Logout', className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ]}
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16 flex flex-col">
        <AdminFilledContentBanner />
        <div className="mb-12 order-1">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none text-text-primary mb-2">
            {user?.business_name || user?.name}
          </h1>
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <p className="text-base text-text-secondary capitalize">{user?.category === 'birthday_decorator' ? 'Birthday Decorator' : user?.category} Vendor</p>
            {user?.display_id && <IdBadge id={user.display_id} label="Vendor ID" testId="vendor-id-badge" />}
          </div>
        </div>

        {/* Booking offers inbox */}
        <div className="mb-8 order-10">
          <VendorOffersCard />
        </div>

        {/* Platform commission visibility */}
        <div className="mb-12 order-11">
          <PlatformCommissionBanner role="vendor" category={user?.category} />
        </div>

        {/* KYC verification card — moved to the bottom (Feb 2026): runs in the background
            while vendor builds out services + serving cities. */}
        <div className="mb-12 order-[90]">
          <KycCard />
        </div>

        {/* Joining fee — Phase B revenue gate. Account becomes fully active once
            KYC is approved AND this one-time fee is paid (with optional offer). */}
        <div className="mb-12 order-[91]">
          <JoiningFeeCard />
        </div>

        {/* Serving cities (Feb 2026) — sits 4th, right after Serving Styles. */}
        <div className="mb-12 order-4">
          <ServingCitiesCard />
        </div>

        {/* Caterer-only — tick which serving styles you offer (traditional / buffet). */}
        {user?.category === 'caterer' && (
          <div className="mb-12 order-3">
            <CatererServiceStylesCard />
          </div>
        )}

        {/* Vendor pending inquiries — confirm availability for customer orders */}
        <div className="mb-12 order-12">
          <VendorPendingInquiriesCard />
        </div>

        {/* Celebrate With Music — multi-service ticklist for music vendors */}
        {user.category === 'nathaswaram_music' && (
          <div className="mb-12 order-[13]">
            <CelebrateMusicSelector />
          </div>
        )}

        {/* Music vendors also act as pros — show their pending offers here */}
        {user.category === 'nathaswaram_music' && (
          <div className="mb-12 order-[14]">
            <ProPendingOffersCard />
          </div>
        )}

        {/* Services + Bookings grid — moved to the TOP (Feb 2026) per user feedback:
            vendors want to start adding services immediately, KYC can happen in parallel. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 order-2">
          {/* Services Section */}
          <div>
            {user?.category === 'caterer' ? (
              // Caterers: the whole "Services" column is replaced by the Menu &
              // Dish Pricing card. AI menu OCR + manual entry IS their service
              // listing — no separate add-service flow needed.
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">Menu &amp; Services</h2>
                </div>
                <CatererMenuCard />
                <CatererServicePriceCard />
              </>
            ) : (
              <>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">{t('vendor.your_services')}</h2>
              <Button
                onClick={() => { resetForm(); setShowServiceForm(!showServiceForm); }}
                className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                data-testid="add-service-btn"
              >
                <Plus size={20} className="mr-2" />
                Add Service
              </Button>
            </div>

            {/* Menu Selector Modal */}
            {showMenuSelector && user.category === 'caterer' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-6xl bg-sand-50 my-8">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Select Menu Items</CardTitle>
                      <Button
                        onClick={() => setShowMenuSelector(false)}
                        variant="ghost"
                        size="sm"
                      >
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Choose items with green (✓) to add, red (✗) to skip. Edit prices as needed.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MenuSelector onMenuSelect={handleQuickMenuSelect} />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Combo Selector Modal */}
            {showComboSelector && user.category === 'caterer' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-6xl bg-sand-50 my-8">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Create Combo Package</CardTitle>
                      <Button
                        onClick={() => setShowComboSelector(false)}
                        variant="ghost"
                        size="sm"
                      >
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Select items, name your combo, and set one price for the entire package.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ComboSelector onComboSelect={handleQuickComboSelect} />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Photographer Selector Modal */}
            {showPhotoSelector && user.category === 'photographer' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-5xl bg-sand-50 my-8 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Add Photography Package</CardTitle>
                      <Button
                        onClick={() => setShowPhotoSelector(false)}
                        variant="ghost"
                        size="sm"
                      >
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Pick event type &rarr; sub-type (if applicable) &rarr; tier &rarr; customize items &amp; price.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PhotographerSelector
                      onPackageAdd={handlePhotoPackageAdd}
                      existingPackageKeys={photoPackages.map(p => `${p.event_type || 'marriage'}__${p.event_subtype || 'none'}__${p.tier}`)}
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Decorator Selector Modal */}
            {showDecorSelector && user.category === 'decorator' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-5xl bg-sand-50 my-8 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Decoration Quick Add</CardTitle>
                      <Button onClick={() => setShowDecorSelector(false)} variant="ghost" size="sm">
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Select marriage types, then pick decoration services. Upload an image and price for each item you select.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DecoratorSelector onSave={handleDecorSave} />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Venue Selector Modal */}
            {showVenueSelector && user.category === 'venue' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-5xl bg-sand-50 my-8 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Venue Quick Add</CardTitle>
                      <Button onClick={() => setShowVenueSelector(false)} variant="ghost" size="sm">
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Select venue types, add photos, capacity details, and catering options.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <VenueSelector onSave={handleVenueSave} />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* DJ Entertainment Selector Modal */}
            {showDjSelector && user.category === 'dj' && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-5xl bg-sand-50 my-8 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">DJ & Entertainment Quick Add</CardTitle>
                      <Button onClick={() => setShowDjSelector(false)} variant="ghost" size="sm">
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Select entertainment types, add photos, pricing, equipment & genres.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DJEntertainmentSelector onSave={handleDjSave} />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Birthday Decorator Selector Modal — used by both birthday_decorator and decorator (optional) */}
            {showBirthdayDecorSelector && (user.category === 'birthday_decorator' || user.category === 'decorator') && (
              <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8 overflow-y-auto">
                <Card className="w-full max-w-5xl bg-sand-50 my-8 max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-3xl">Birthday Decoration Quick Add</CardTitle>
                      <Button onClick={() => setShowBirthdayDecorSelector(false)} variant="ghost" size="sm">
                        <X size={24} />
                      </Button>
                    </div>
                    <CardDescription>Select birthday themes, add decoration add-ons with photos & pricing.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BirthdayDecoratorSelector onSave={handleBirthdayDecorSave} />
                  </CardContent>
                </Card>
              </div>
            )}

            {showServiceForm && (
              <Card className="mb-6 bg-white border-[#E8E1D5]">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="font-heading text-2xl">
                      {editingService ? 'Edit Service' : 'Create New Service'}
                    </CardTitle>
                    <Button
                      onClick={() => { setShowServiceForm(false); resetForm(); }}
                      variant="ghost"
                      size="sm"
                    >
                      <X size={20} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateOrUpdateService} className="space-y-4">
                    {/* Caterer-specific fields */}
                    {user.category === 'caterer' && (
                      <>
                        <div className="pt-2">
                          <div className="flex justify-between items-center mb-3">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Menu Items</Label>
                            <Button 
                              type="button" 
                              onClick={() => setShowMenuSelector(true)} 
                              size="sm" 
                              className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                              data-testid="quick-add-menu-btn"
                            >
                              <ListChecks size={16} className="mr-1" /> Quick Add from Menu
                            </Button>
                          </div>
                          {menuItems.filter(i => i.name).length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {menuItems.filter(i => i.name).map((item, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-300 text-sm rounded">
                                  {item.name} <span className="text-brand-primary font-semibold">₹{item.price}</span>
                                  <button type="button" onClick={() => removeMenuItem(index)} className="text-red-400 hover:text-red-600 ml-1">
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-text-secondary">No items yet. Use Quick Add to select.</p>
                          )}
                        </div>

                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <div className="flex justify-between items-center mb-3">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Combo Packages</Label>
                            <Button 
                              type="button" 
                              onClick={() => setShowComboSelector(true)} 
                              size="sm" 
                              className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                              data-testid="quick-add-combo-btn"
                            >
                              <ListChecks size={16} className="mr-1" /> Quick Add Combo
                            </Button>
                          </div>
                          {combos.filter(c => c.name).length > 0 ? (
                            <div className="space-y-2">
                              {combos.filter(c => c.name).map((combo, index) => (
                                <div key={index} className="flex items-center gap-2 p-2 border border-green-300 bg-green-50 rounded">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium">{combo.name}</div>
                                    <div className="text-xs text-text-secondary">{combo.items}</div>
                                  </div>
                                  <span className="text-sm font-semibold text-brand-primary">₹{combo.price}</span>
                                  <button type="button" onClick={() => removeCombo(index)} className="text-red-400 hover:text-red-600">
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-text-secondary">No combos yet. Use Quick Add to create.</p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Photographer-specific fields */}
                    {user.category === 'photographer' && (
                      <>
                        {/* Quick Add Button */}
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Button
                            type="button"
                            onClick={() => setShowPhotoSelector(true)}
                            disabled={photoPackages.length >= 40}
                            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none mb-2"
                            data-testid="quick-add-photo-btn"
                          >
                            <Plus size={18} className="mr-2" /> Add Package ({photoPackages.length} added)
                          </Button>
                          {photoPackages.length === 0 && (
                            <p className="text-xs text-text-secondary text-center">Add packages scoped by event type &amp; sub-type</p>
                          )}
                        </div>

                        {/* Editable Package Cards */}
                        {photoPackages.map((pkg, pkgIndex) => {
                          const tierStyles = {
                            budget: { border: 'border-green-500', bg: 'bg-green-50', badge: 'bg-green-600' },
                            luxury: { border: 'border-amber-500', bg: 'bg-amber-50', badge: 'bg-amber-600' },
                            premium: { border: 'border-purple-500', bg: 'bg-purple-50', badge: 'bg-purple-600' },
                            ultra_luxury: { border: 'border-rose-500', bg: 'bg-rose-50', badge: 'bg-rose-600' },
                          };
                          const s = tierStyles[pkg.tier] || tierStyles.budget;
                          return (
                            <div key={pkgIndex} className={`p-4 border-2 ${s.border} ${s.bg} mb-4`} data-testid={`photo-pkg-card-${pkg.event_type || 'marriage'}-${pkg.event_subtype || 'none'}-${pkg.tier}`}>
                              {/* Header */}
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${s.badge}`}>
                                    {pkg.name}
                                  </span>
                                  {pkg.event_type_label && (
                                    <span className="text-xs px-2 py-0.5 bg-text-primary text-sand-50 font-bold uppercase tracking-wider">
                                      {pkg.event_type_label}
                                    </span>
                                  )}
                                  {pkg.event_subtype_label && (
                                    <span className="text-xs px-2 py-0.5 bg-brand-primary text-sand-50 font-bold uppercase tracking-wider">
                                      {pkg.event_subtype_label}
                                    </span>
                                  )}
                                  <span className="text-xs text-text-secondary">{pkg.description}</span>
                                </div>
                                <Button type="button" onClick={() => removePhotoPackage(pkgIndex)} variant="ghost" size="sm" data-testid={`remove-pkg-${pkg.tier}-${pkgIndex}`}>
                                  <X size={18} />
                                </Button>
                              </div>

                              {/* Editable Price */}
                              <div className="flex items-center gap-2 mb-4">
                                <span className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Package Price</span>
                                <div className="flex items-center">
                                  <span className="font-heading text-xl text-brand-primary mr-1">₹</span>
                                  <Input
                                    type="number"
                                    value={pkg.price}
                                    onChange={(e) => updatePhotoPackagePrice(pkgIndex, e.target.value)}
                                    className="rounded-none border-[#E8E1D5] w-36 font-heading text-xl"
                                    data-testid={`pkg-price-${pkg.tier}`}
                                  />
                                </div>
                              </div>

                              {/* Items with toggle + qty */}
                              <div className="space-y-2">
                                {pkg.items.map((item, itemIdx) => (
                                  <div key={itemIdx} className={`flex items-center gap-2 p-2 rounded border ${item.qty > 0 ? 'border-green-400 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
                                    <Button
                                      type="button"
                                      onClick={() => removePhotoPackageItem(pkgIndex, itemIdx)}
                                      className="rounded-full w-7 h-7 p-0 bg-red-500 hover:bg-red-600 text-white flex-shrink-0"
                                    >
                                      <X size={12} weight="bold" />
                                    </Button>
                                    <span className="flex-1 text-sm font-medium">{item.name}</span>
                                    {item.category === 'Instruments' && (
                                      <div className="flex items-center gap-1">
                                        <Button type="button" onClick={() => changePhotoPackageItemQty(pkgIndex, itemIdx, -1)} variant="outline" size="sm" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]">
                                          <Minus size={12} />
                                        </Button>
                                        <span className="w-6 text-center text-sm font-bold">{item.qty}</span>
                                        <Button type="button" onClick={() => changePhotoPackageItemQty(pkgIndex, itemIdx, 1)} variant="outline" size="sm" className="w-7 h-7 p-0 rounded-none border-[#E8E1D5]">
                                          <Plus size={12} />
                                        </Button>
                                      </div>
                                    )}
                                    {item.category !== 'Instruments' && (
                                      <span className="text-xs text-text-secondary">x{item.qty}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Decorator-specific fields */}
                    {user.category === 'decorator' && (
                      <>
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block" data-testid="grand-detailed-decoration-label">
                            Grand Detailed Decoration
                          </Label>
                          <Button
                            type="button"
                            onClick={() => setShowDecorSelector(true)}
                            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none mb-2"
                            data-testid="quick-add-decor-btn"
                          >
                            <Plus size={18} className="mr-2" /> Quick Add - Event Packages
                          </Button>
                        </div>

                        {/* Display decorator packages (new event-scoped model) */}
                        {decorData && decorData.packages?.length > 0 && (
                          <div className="space-y-3 pt-4 border-t border-[#E8E1D5]">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Saved Packages</Label>
                            {decorData.packages.map((p, idx) => {
                              const EVENT_LABELS = { marriage: 'Marriage', engagement: 'Engagement / Reception', birthday: 'Birthday', baby_shower: 'Baby Shower', house_warming: 'House Warming', corporate: 'Corporate', anniversary: 'Anniversary', puberty_ceremony: 'Puberty Ceremony', retirement: 'Retirement', religious: 'Religious / Pooja', other: 'Other' };
                              const TRAD_LABELS = { hindu: 'Hindu', muslim: 'Muslim', christian: 'Christian', brahmin: 'Brahmin' };
                              return (
                                <div key={idx} className="border-2 border-green-400 bg-green-50/30 rounded p-3" data-testid={`saved-package-${idx}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="flex-1 font-heading text-lg font-medium">{p.name || `Package ${idx + 1}`}</span>
                                    {Number(p.price) > 0 && (
                                      <span className="font-heading text-xl text-brand-primary">₹{Number(p.price).toLocaleString('en-IN')}</span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-xs text-text-secondary mb-2">
                                    <span className="bg-white px-2 py-0.5 rounded border border-[#E8E1D5]"><strong>Event:</strong> {EVENT_LABELS[p.event_type] || p.event_type}</span>
                                    {(p.traditions || []).length > 0 && (
                                      <span className="bg-white px-2 py-0.5 rounded border border-[#E8E1D5]"><strong>Traditions:</strong> {p.traditions.map(t => TRAD_LABELS[t] || t).join(', ')}</span>
                                    )}
                                    {(p.venue_types || []).length > 0 && (
                                      <span className="bg-white px-2 py-0.5 rounded border border-[#E8E1D5]"><strong>Venues:</strong> {p.venue_types.join(', ')}</span>
                                    )}
                                    {p.fresh_flowers && (
                                      <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded border border-yellow-300"><strong>Fresh Flowers</strong></span>
                                    )}
                                  </div>
                                  {(p.photos || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {p.photos.slice(0, 5).map((ph, pi) => (
                                        <img key={pi} src={ph.startsWith('http') ? ph : `${BACKEND_URL}${ph}`} alt="" className="w-16 h-16 object-cover rounded border border-[#E8E1D5]" />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Optional Birthday Themes for decorators (cross-category support) */}
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Button
                            type="button"
                            onClick={() => setShowBirthdayDecorSelector(true)}
                            className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-none mb-2"
                            data-testid="quick-add-decorator-birthday-btn"
                          >
                            <Plus size={18} className="mr-2" /> Quick Add - Birthday Themes (optional)
                          </Button>
                          <p className="text-xs text-text-secondary">Offering birthday decoration too? List your themes — they appear in the customer Birthday flow.</p>
                        </div>

                        {/* Simple Wedding Decoration (optional) — formerly Custom Wedding Packages */}
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block" data-testid="simple-wedding-decoration-label">Simple Wedding Decoration</Label>
                          <CustomDecorPackages
                            value={customDecorPackages}
                            onChange={setCustomDecorPackages}
                          />
                        </div>

                        {/* Display birthday themes saved by this decorator */}
                        {birthdayDecorData && birthdayDecorData.themes?.length > 0 && (
                          <div className="space-y-3 pt-4 border-t border-[#E8E1D5]">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Selected Birthday Themes</Label>
                            {birthdayDecorData.themes.map((t, idx) => (
                              <div key={idx} className="border-2 border-pink-400 bg-pink-50/30 rounded p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  {t.photo && <img src={`${BACKEND_URL}${t.photo}`} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />}
                                  <span className="flex-1 font-heading text-lg font-medium">
                                    {t.custom && <span className="text-[10px] px-1.5 py-0.5 bg-pink-600 text-white rounded font-bold uppercase tracking-wider mr-1">Custom</span>}
                                    {t.name}
                                  </span>
                                  <span className="font-heading text-xl text-brand-primary">
                                    ₹{((parseInt(t.price) || 0) + (t.addons || []).reduce((sum, a) => sum + (parseInt(a.price) || 0), 0)).toLocaleString('en-IN')}
                                  </span>
                                </div>
                                {t.addons?.length > 0 && (
                                  <div className="ml-4">
                                    <span className="text-xs font-bold text-text-secondary">Add-ons: </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {t.addons.map((a, aIdx) => (
                                        <span key={aIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded inline-flex items-center gap-1">
                                          {a.photo && <img src={`${BACKEND_URL}${a.photo}`} alt="" className="w-5 h-5 object-cover rounded" />}
                                          {a.name} <span className="text-brand-primary font-semibold">₹{Number(a.price).toLocaleString('en-IN')}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Venue-specific fields */}
                    {user.category === 'venue' && (
                      <>
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Button
                            type="button"
                            onClick={() => setShowVenueSelector(true)}
                            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none mb-2"
                            data-testid="quick-add-venue-btn"
                          >
                            <Plus size={18} className="mr-2" /> Quick Add - Venue Types & Details
                          </Button>
                        </div>

                        {/* Display venue data */}
                        {venueData && venueData.venues?.length > 0 && (
                          <div className="space-y-3 pt-4 border-t border-[#E8E1D5]">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Selected Venue Types</Label>
                            {venueData.venues.map((v, idx) => (
                              <div key={idx} className="border-2 border-green-400 bg-green-50/30 rounded p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="flex-1 font-heading text-lg font-medium">{v.name}</span>
                                  {v.has_ac && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">AC</span>}
                                </div>
                                <div className="flex flex-wrap gap-3 text-sm text-text-secondary mb-2">
                                  <span>{v.sqft} sq.ft</span>
                                  <span>{v.guest_capacity} guests max</span>
                                  {v.has_rooms && <span>{v.single_rooms} single + {v.double_rooms} double rooms</span>}
                                </div>
                                {v.photos?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {v.photos.map((p, pIdx) => (
                                      <img key={pIdx} src={`${BACKEND_URL}${p}`} alt="" className="w-12 h-12 object-cover rounded border border-[#E8E1D5]" />
                                    ))}
                                  </div>
                                )}
                                {v.has_catering && (
                                  <div className="border-t border-[#E8E1D5] pt-2 mt-2">
                                    <span className="text-xs font-bold text-text-secondary">Catering (min {v.min_guests} guests):</span>
                                    <div className="space-y-2 mt-1">
                                      {['budget', 'premium', 'luxury'].map(tier => {
                                        const t = v.catering_tiers[tier];
                                        if (!t) return null;
                                        const hasData = t.morning_tiffin || t.lunch_veg || t.lunch_nonveg || t.evening_snacks || t.dinner_veg || t.dinner_nonveg || t.juice;
                                        if (!hasData) return null;
                                        const tierLabel = tier === 'budget' ? 'Budget' : tier === 'premium' ? 'Premium' : 'Luxury';
                                        const tierColor = tier === 'budget' ? 'bg-green-600' : tier === 'premium' ? 'bg-purple-600' : 'bg-amber-600';
                                        return (
                                          <div key={tier} className="text-xs border border-[#E8E1D5] rounded p-2 bg-white">
                                            <span className={`${tierColor} text-white px-1.5 py-0.5 font-bold uppercase tracking-wider`}>{tierLabel}</span>
                                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-text-secondary">
                                              {t.morning_tiffin && <span>Tiffin ₹{Number(t.morning_tiffin).toLocaleString('en-IN')}</span>}
                                              {t.lunch_veg && <span>Lunch Veg ₹{Number(t.lunch_veg).toLocaleString('en-IN')}</span>}
                                              {t.lunch_nonveg && <span>Lunch NV ₹{Number(t.lunch_nonveg).toLocaleString('en-IN')}</span>}
                                              {t.evening_snacks && <span>Snacks ₹{Number(t.evening_snacks).toLocaleString('en-IN')}</span>}
                                              {t.dinner_veg && <span>Dinner Veg ₹{Number(t.dinner_veg).toLocaleString('en-IN')}</span>}
                                              {t.dinner_nonveg && <span>Dinner NV ₹{Number(t.dinner_nonveg).toLocaleString('en-IN')}</span>}
                                              {t.juice && <span>Juice ₹{Number(t.juice).toLocaleString('en-IN')}</span>}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* DJ-specific fields */}
                    {user.category === 'dj' && (
                      <>
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Button
                            type="button"
                            onClick={() => setShowDjSelector(true)}
                            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none mb-2"
                            data-testid="quick-add-dj-btn"
                          >
                            <Plus size={18} className="mr-2" /> Quick Add - DJ & Entertainment Types
                          </Button>
                        </div>

                        {/* Display DJ data */}
                        {djData && djData.entertainment_types?.length > 0 && (
                          <div className="space-y-3 pt-4 border-t border-[#E8E1D5]">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Selected Entertainment Types</Label>
                            {djData.entertainment_types.map((t, idx) => (
                              <div key={idx} className="border-2 border-green-400 bg-green-50/30 rounded p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="flex-1 font-heading text-lg font-medium">{t.name}</span>
                                  <span className="text-xs text-text-secondary">{t.num_performers} performer(s)</span>
                                </div>
                                <div className="flex flex-wrap gap-3 text-sm text-text-secondary mb-2">
                                  {t.price_per_event && <span>₹{Number(t.price_per_event).toLocaleString('en-IN')}/event</span>}
                                  {t.price_per_hour && <span>₹{Number(t.price_per_hour).toLocaleString('en-IN')}/hr</span>}
                                  {t.specialization && <span>{t.specialization}</span>}
                                </div>
                                {t.photos?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {t.photos.map((p, pIdx) => (
                                      <img key={pIdx} src={`${BACKEND_URL}${p}`} alt="" className="w-12 h-12 object-cover rounded border border-[#E8E1D5]" />
                                    ))}
                                  </div>
                                )}
                                {t.selectedEquipment?.length > 0 && (
                                  <div className="mb-1">
                                    <span className="text-xs font-bold text-text-secondary">Equipment: </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {t.selectedEquipment.map((eq, eIdx) => (
                                        <span key={eIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded">
                                          {eq.name} {eq.included ? '(Included)' : `₹${Number(eq.price).toLocaleString('en-IN')}`}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {t.selectedGenres?.length > 0 && (
                                  <div>
                                    <span className="text-xs font-bold text-text-secondary">Genres: </span>
                                    <span className="text-xs text-text-secondary">{t.selectedGenres.join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Birthday Decorator-specific fields */}
                    {user.category === 'birthday_decorator' && (
                      <>
                        <div className="pt-4 border-t border-[#E8E1D5]">
                          <Button
                            type="button"
                            onClick={() => setShowBirthdayDecorSelector(true)}
                            className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-none mb-2"
                            data-testid="quick-add-birthday-decor-btn"
                          >
                            <Plus size={18} className="mr-2" /> Quick Add - Birthday Themes & Decorations
                          </Button>
                        </div>

                        {/* Display birthday decor data */}
                        {birthdayDecorData && birthdayDecorData.themes?.length > 0 && (
                          <div className="space-y-3 pt-4 border-t border-[#E8E1D5]">
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Selected Birthday Themes</Label>
                            {birthdayDecorData.themes.map((t, idx) => (
                              <div key={idx} className="border-2 border-pink-400 bg-pink-50/30 rounded p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  {t.photo && <img src={`${BACKEND_URL}${t.photo}`} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />}
                                  <span className="flex-1 font-heading text-lg font-medium">{t.name}</span>
                                  <span className="font-heading text-xl text-brand-primary">
                                    ₹{((parseInt(t.price) || 0) + (t.addons || []).reduce((sum, a) => sum + (parseInt(a.price) || 0), 0)).toLocaleString('en-IN')}
                                  </span>
                                </div>
                                {t.addons?.length > 0 && (
                                  <div className="ml-4">
                                    <span className="text-xs font-bold text-text-secondary">Add-ons: </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {t.addons.map((a, aIdx) => (
                                        <span key={aIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded inline-flex items-center gap-1">
                                          {a.photo && <img src={`${BACKEND_URL}${a.photo}`} alt="" className="w-5 h-5 object-cover rounded" />}
                                          {a.name} <span className="text-brand-primary font-semibold">₹{Number(a.price).toLocaleString('en-IN')}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex gap-4 pt-4">
                      <Button type="submit" className="flex-1 bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-service-btn">
                        {editingService ? 'Update Service' : 'Create Service'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => { setShowServiceForm(false); resetForm(); }}
                        className="flex-1 bg-transparent border border-text-primary text-text-primary hover:bg-text-primary hover:text-sand-50 rounded-none"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4">
              {services.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No services yet. Create your first service to get started!</p>
                  </CardContent>
                </Card>
              ) : (
                services.map((service) => (
                  <Card key={service.id} className="bg-white border-[#E8E1D5]" data-testid={`service-card-${service.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="font-heading text-2xl">{service.name}</CardTitle>
                          <CardDescription>{service.description}</CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditService(service)}
                            variant="ghost"
                            size="sm"
                            className="text-brand-primary hover:text-brand-primary hover:bg-brand-accent/20"
                            data-testid={`edit-service-${service.id}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteService(service.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-service-${service.id}`}
                          >
                            <X size={18} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="font-heading text-3xl text-brand-primary mb-4">₹{service.base_price}</div>
                      
                      {service.packages && service.packages.length > 0 && (
                        <div className="space-y-3 text-sm">
                          {service.packages[0].menu_items && (
                            <div>
                              <div className="font-bold text-text-secondary mb-1">Menu Items:</div>
                              <div className="space-y-1">
                                {service.packages[0].menu_items.map((item, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span>{item.name}</span>
                                    <span className="text-brand-primary">₹{item.price}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {service.packages[0].combos && (
                            <div>
                              <div className="font-bold text-text-secondary mb-1">Combos:</div>
                              <div className="space-y-2">
                                {service.packages[0].combos.map((combo, idx) => (
                                  <div key={idx} className="border-l-2 border-brand-primary pl-2">
                                    <div className="flex justify-between font-medium">
                                      <span>{combo.name}</span>
                                      <span className="text-brand-primary">₹{combo.price}</span>
                                    </div>
                                    <div className="text-xs text-text-secondary">{combo.items}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {service.packages[0].photo_packages && service.packages[0].photo_packages.length > 0 && (
                            <div>
                              <div className="font-bold text-text-secondary mb-2">Packages:</div>
                              <div className="space-y-3">
                                {service.packages[0].photo_packages.map((pkg, idx) => {
                                  const tierColors = { budget: 'bg-green-600', luxury: 'bg-amber-600', premium: 'bg-purple-600', ultra_luxury: 'bg-rose-600' };
                                  return (
                                    <div key={idx} className="border border-[#E8E1D5] p-3">
                                      <div className="flex justify-between items-center mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tierColors[pkg.tier] || 'bg-gray-600'}`}>
                                            {pkg.name}
                                          </span>
                                          <span className="text-xs text-text-secondary">{pkg.description}</span>
                                        </div>
                                        <span className="font-heading text-xl text-brand-primary">₹{Number(pkg.price).toLocaleString('en-IN')}</span>
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {pkg.items.map((item, iIdx) => (
                                          <span key={iIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-1">
                                            {item.name}{item.qty > 1 ? ` x${item.qty}` : ''}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Decorator data display - nested under marriage types */}
                          {service.packages[0].decor_data?.marriage_types?.length > 0 && (
                            <div>
                              <div className="font-bold text-text-secondary mb-2">Marriage Types:</div>
                              {service.packages[0].decor_data.marriage_types.map((m, idx) => (
                                <div key={idx} className="border border-[#E8E1D5] p-3 rounded mb-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    {m.photo && <img src={`${BACKEND_URL}${m.photo}`} alt="" className="w-10 h-10 object-cover rounded" />}
                                    <span className="flex-1 font-medium">{m.name}</span>
                                    <span className="text-brand-primary font-heading text-lg">₹{Number(m.price).toLocaleString('en-IN')}</span>
                                  </div>
                                  {m.venues?.length > 0 && (
                                    <div className="ml-4 mb-1">
                                      <span className="text-xs font-bold text-text-secondary">Venue Decorations:</span>
                                      {m.venues.map((v, vIdx) => (
                                        <div key={vIdx} className="mt-1 text-xs">
                                          <span className="font-medium">{v.name}</span> — {v.designs?.length || 0} design(s)
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {v.designs?.map((design, dIdx) => (
                                              <span key={dIdx} className="inline-flex items-center gap-1 bg-white border border-[#E8E1D5] px-2 py-0.5 rounded">
                                                {design.photo && <img src={`${BACKEND_URL}${design.photo}`} alt="" className="w-4 h-4 object-cover rounded" />}
                                                {design.label} <span className="text-brand-primary font-semibold">₹{Number(design.price).toLocaleString('en-IN')}</span>
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {m.decorations?.length > 0 && (
                                    <div className="ml-4">
                                      <span className="text-xs font-bold text-text-secondary">Decorations: </span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {m.decorations.map((d, dIdx) => (
                                          <span key={dIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded">
                                            {d.name} ₹{Number(d.price).toLocaleString('en-IN')}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Venue data display */}
                          {service.packages[0].venue_data?.venues?.length > 0 && (
                            <div>
                              <div className="font-bold text-text-secondary mb-2">Venue Types:</div>
                              {service.packages[0].venue_data.venues.map((v, idx) => (
                                <div key={idx} className="border border-[#E8E1D5] p-3 rounded mb-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="flex-1 font-medium">{v.name}</span>
                                    {v.has_ac && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">AC</span>}
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-xs text-text-secondary mb-2">
                                    <span>{v.sqft} sq.ft</span>
                                    <span>{v.guest_capacity} guests max</span>
                                    {v.has_rooms && <span>{v.single_rooms} single + {v.double_rooms} double rooms</span>}
                                  </div>
                                  {/* Event-specific rental pricing */}
                                  {v.event_prices && (
                                    <div className="bg-brand-accent/5 border border-brand-primary/20 rounded p-2 mb-2">
                                      <div className="text-[10px] tracking-wider uppercase font-bold text-brand-primary mb-1">Rental Pricing</div>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1 text-xs">
                                        {v.event_prices.marriage && (
                                          <div className="text-text-primary"><strong>Marriage/Eng/Rec:</strong> ₹{Number(v.event_prices.marriage).toLocaleString('en-IN')}</div>
                                        )}
                                        {Object.entries(v.event_prices)
                                          .filter(([k, val]) => k !== 'marriage' && val && Number(val) > 0)
                                          .map(([k, val]) => {
                                            const labels = { birthday: 'Birthday', baby_shower: 'Baby Shower', house_warming: 'House Warming', anniversary: 'Anniversary', corporate: 'Corporate', puberty_ceremony: 'Puberty Ceremony', retirement: 'Retirement', religious: 'Religious', other: 'Other' };
                                            return (
                                              <div key={k} className="text-text-primary"><strong>{labels[k] || k}:</strong> ₹{Number(val).toLocaleString('en-IN')}</div>
                                            );
                                          })}
                                      </div>
                                    </div>
                                  )}
                                  {v.photos?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {v.photos.map((p, pIdx) => (
                                        <img key={pIdx} src={`${BACKEND_URL}${p}`} alt="" className="w-10 h-10 object-cover rounded border border-[#E8E1D5]" />
                                      ))}
                                    </div>
                                  )}
                                  {v.has_catering && (
                                    <div className="border-t border-[#E8E1D5] pt-2 mt-1">
                                      <span className="text-xs font-bold text-text-secondary">Catering (min {v.min_guests} guests):</span>
                                      <div className="space-y-1 mt-1">
                                        {['budget', 'premium', 'luxury'].map(tier => {
                                          const t = v.catering_tiers?.[tier];
                                          if (!t) return null;
                                          const hasData = t.morning_tiffin || t.lunch_veg || t.lunch_nonveg || t.evening_snacks || t.dinner_veg || t.dinner_nonveg || t.juice;
                                          if (!hasData) return null;
                                          const tierLabel = tier === 'budget' ? 'Budget' : tier === 'premium' ? 'Premium' : 'Luxury';
                                          const tierColor = tier === 'budget' ? 'bg-green-600' : tier === 'premium' ? 'bg-purple-600' : 'bg-amber-600';
                                          return (
                                            <div key={tier} className="text-xs border border-[#E8E1D5] rounded p-2 bg-white">
                                              <span className={`${tierColor} text-white px-1.5 py-0.5 font-bold uppercase tracking-wider`}>{tierLabel}</span>
                                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-text-secondary">
                                                {t.morning_tiffin && <span>Tiffin ₹{Number(t.morning_tiffin).toLocaleString('en-IN')}</span>}
                                                {t.lunch_veg && <span>Lunch Veg ₹{Number(t.lunch_veg).toLocaleString('en-IN')}</span>}
                                                {t.lunch_nonveg && <span>Lunch NV ₹{Number(t.lunch_nonveg).toLocaleString('en-IN')}</span>}
                                                {t.evening_snacks && <span>Snacks ₹{Number(t.evening_snacks).toLocaleString('en-IN')}</span>}
                                                {t.dinner_veg && <span>Dinner Veg ₹{Number(t.dinner_veg).toLocaleString('en-IN')}</span>}
                                                {t.dinner_nonveg && <span>Dinner NV ₹{Number(t.dinner_nonveg).toLocaleString('en-IN')}</span>}
                                                {t.juice && <span>Juice ₹{Number(t.juice).toLocaleString('en-IN')}</span>}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* DJ Entertainment data display */}
                          {service.packages[0].dj_data?.entertainment_types?.length > 0 && (
                            <div>
                              <div className="font-bold text-text-secondary mb-2">Entertainment Types:</div>
                              {service.packages[0].dj_data.entertainment_types.map((t, idx) => (
                                <div key={idx} className="border border-[#E8E1D5] p-3 rounded mb-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="flex-1 font-medium">{t.name}</span>
                                    <span className="text-xs text-text-secondary">{t.num_performers} performer(s)</span>
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-xs text-text-secondary mb-2">
                                    {t.price_per_event && <span>₹{Number(t.price_per_event).toLocaleString('en-IN')}/event</span>}
                                    {t.price_per_hour && <span>₹{Number(t.price_per_hour).toLocaleString('en-IN')}/hr</span>}
                                    {t.specialization && <span>{t.specialization}</span>}
                                  </div>
                                  {t.photos?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {t.photos.map((p, pIdx) => (
                                        <img key={pIdx} src={`${BACKEND_URL}${p}`} alt="" className="w-10 h-10 object-cover rounded border border-[#E8E1D5]" />
                                      ))}
                                    </div>
                                  )}
                                  {t.selectedEquipment?.length > 0 && (
                                    <div className="mb-1">
                                      <span className="text-xs font-bold text-text-secondary">Equipment: </span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {t.selectedEquipment.map((eq, eIdx) => (
                                          <span key={eIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded">
                                            {eq.name} {eq.included ? '(Included)' : `₹${Number(eq.price).toLocaleString('en-IN')}`}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {t.selectedGenres?.length > 0 && (
                                    <div>
                                      <span className="text-xs font-bold text-text-secondary">Genres: </span>
                                      <span className="text-xs text-text-secondary">{t.selectedGenres.join(', ')}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Birthday Decorator data display */}
                          {service.packages[0].birthday_decor_data?.themes?.length > 0 && (
                            <div>
                              <div className="font-bold text-text-secondary mb-2">Birthday Themes:</div>
                              {service.packages[0].birthday_decor_data.themes.map((t, idx) => (
                                <div key={idx} className="border border-pink-300 bg-pink-50/30 p-3 rounded mb-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    {t.photo && <img src={`${BACKEND_URL}${t.photo}`} alt="" className="w-10 h-10 object-cover rounded" />}
                                    <span className="flex-1 font-medium">{t.name}</span>
                                    <span className="text-brand-primary font-heading text-lg">
                                      ₹{((parseInt(t.price) || 0) + (t.addons || []).reduce((sum, a) => sum + (parseInt(a.price) || 0), 0)).toLocaleString('en-IN')}
                                    </span>
                                  </div>
                                  {t.addons?.length > 0 && (
                                    <div className="ml-4">
                                      <span className="text-xs font-bold text-text-secondary">Add-ons: </span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {t.addons.map((a, aIdx) => (
                                          <span key={aIdx} className="text-xs bg-white border border-[#E8E1D5] px-2 py-0.5 rounded">
                                            {a.name} ₹{Number(a.price).toLocaleString('en-IN')}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
              </>
            )}
          </div>

          {/* Bookings Section */}
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mb-8">{t('vendor.bookings')}</h2>
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No booking requests yet</p>
                  </CardContent>
                </Card>
              ) : (
                bookings.map((booking) => (
                  <Card key={booking.id} className="bg-white border-[#E8E1D5]" data-testid={`booking-card-${booking.id}`}>
                    <CardHeader>
                      <CardTitle className="font-heading text-xl">{booking.customer_name}</CardTitle>
                      <CardDescription>{booking.service_name}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Calendar size={16} weight="duotone" />
                        <span>{new Date(booking.event_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Package size={16} weight="duotone" />
                        <span className="capitalize">{booking.event_type}</span>
                      </div>
                      <div className="font-heading text-2xl text-brand-primary">₹{booking.total_price}</div>
                      {booking.notes && (
                        <div className="text-sm text-text-secondary">
                          <div className="font-bold">Notes:</div>
                          <p>{booking.notes}</p>
                        </div>
                      )}
                      <div className="flex gap-2 mt-4">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                              className="flex-1 bg-brand-secondary hover:bg-[#3A4D3E] text-sand-50 rounded-none"
                              data-testid={`confirm-booking-${booking.id}`}
                            >
                              Confirm
                            </Button>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                              className="flex-1 bg-transparent border border-text-primary text-text-primary hover:bg-text-primary hover:text-sand-50 rounded-none"
                              data-testid={`cancel-booking-${booking.id}`}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                        {booking.status !== 'pending' && (
                          <div className="text-sm font-bold uppercase tracking-wider text-text-secondary">
                            Status: {booking.status}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Calendar Availability — order-5 per Feb 2026 dashboard reshuffle. */}
        <div className="order-5 mb-8">
          <CalendarAvailability />
        </div>

        {/* Portfolio Gallery — universal media library for ALL vendors + venue
            providers (Feb 2026). Caps are admin-controlled per role.
            Caterer's existing caterer-menu-card already covers their dish flow,
            but they still get a portfolio for general-purpose photos. */}
        <div className="order-[7] mb-8">
          <h2 className="font-heading text-2xl mb-4">Portfolio Gallery</h2>
          <ProPortfolioCard />
        </div>

        {/* Cake Catalogue — only for Cake Vendors */}
        {user?.category === 'cake_vendor' && (
          <div className="order-[7] mb-8" data-testid="vendor-cake-catalogue-section">
            <CakePackagesCard />
          </div>
        )}

        {/* Catalog Editor — for Return Gift / Flowers & Pooja vendors */}
        {(user?.category === 'return_gift' || user?.category === 'flowers_pooja') && (
          <div className="order-[7] mb-8" data-testid="vendor-catalog-section">
            <VendorCatalogEditor vendorCategory={user.category} />
          </div>
        )}

        {/* Order lifecycle inbox — Accept/Reject/Complete + customer phone */}
        <div className="order-[8] mb-8">
          <BookingsBoard viewerRole="vendor" title="Booking Requests" testId="vendor-bookings-board" />
        </div>

        {/* Meta (Instagram + Facebook) connect — import photos + auto-post bookings */}
        <div className="order-[8] mb-8">
          <MetaSocialConnectCard />
        </div>

        {/* Google Maps photo importer — vendors & venues only (Pros are excluded server-side) */}
        <div className="order-[8] mb-8">
          <GoogleMapsImportCard />
        </div>

        {/* Customer Inquiries — every saved-package cart line that names this vendor.
            Shows customer phone + Mark Booked button. The simplified "booking" loop. */}
        <div className="order-[4] mb-8">
          <CustomerInquiriesPanel />
        </div>

        {/* Buy Leads + Monthly Lift Pass */}
        <div className="order-[6] mb-8">
          <BuyLeadsCard />
        </div>

        {/* Products catalog (only flowers_pooja / return_gift vendors;
            the panel hides itself for everyone else via /api/me/products → 403) */}
        <div className="order-[3] mb-8">
          <VendorProductsPanel />
        </div>

        {/* Photographer Work Gallery (photographer only) */}
        {user?.category === 'photographer' && (
          <div className="order-[15] mb-8">
            <PhotographerGallery />
          </div>
        )}

        {/* Event assignments + replacement pool */}
        <div className="order-[16] mb-8">
          <ProAttendanceWidget />
        </div>

        {/* Invite & Earn — order-6 per Feb 2026 dashboard reshuffle. */}
        <div className="order-6 mt-10 mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mb-6">Invite &amp; Earn</h2>
          <InviteAndEarn />
        </div>

        <div className="order-[99]">
          <DeleteAccountSection />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VendorDashboard;
